import dayjs, { Dayjs } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isTodayPlugin from 'dayjs/plugin/isToday'
import isYesterdayPlugin from 'dayjs/plugin/isYesterday'
import localeData from 'dayjs/plugin/localeData'
import relativeTime from 'dayjs/plugin/relativeTime'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import 'dayjs/locale/ro'
import 'dayjs/locale/ru'

dayjs.extend(duration)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)
dayjs.extend(isTodayPlugin)
dayjs.extend(isYesterdayPlugin)
dayjs.extend(isBetween)
dayjs.extend(advancedFormat)
dayjs.extend(localeData)
dayjs.extend(weekday)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

export const DEFAULT_TIME_FORMAT = 'HH:mm'
export const DEFAULT_FULL_TIME_FORMAT = 'HH:mm:ss'
export const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY'
export const DATE_FORMAT = 'DD-MM-YYYY'
export const DEFAULT_ISO_DATE_FORMAT = 'YYYY-MM-DD'
export const FULL_DATE_FORMAT = 'dddd, DD MMMM, YYYY'
export const DEFAULT_FULL_DATE_FORMAT = 'DD MMMM, YYYY'
export const DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm'
export const DATE_TIME_FORMAT_API = 'YYYY-MM-DD HH:mm'
export const MONTH_AND_YEAR = 'MMMM YYYY'
export const dateFormatInput = 'dd.MM.yyyy'
export const dateTimeFormatInput = 'dd.MM.yyyy HH:mm'
export const MONTH_FORMAT = 'MMMM'
export const YEAR_FORMAT = 'YYYY'

export const today = dayjs()
export const tomorrow = dayjs().add(1, 'day')
export const yesterday = dayjs().subtract(1, 'day')

export const isToday = (date: dayjs.ConfigType): boolean =>
  dayjs(date).isToday()

export const isYesterday = (date: dayjs.ConfigType): boolean =>
  dayjs(date).subtract(1, 'day').isYesterday()

export const displayDate = (date: dayjs.ConfigType): string =>
  dayjs(date).format(DEFAULT_DATE_FORMAT)

export const displayTime = (date: dayjs.ConfigType): string =>
  dayjs(date).format(DEFAULT_TIME_FORMAT)

export const format = (
  date: dayjs.ConfigType,
  formatStr?: string,
  inputFormat?: string
): string => dayjs(date, inputFormat).format(formatStr || DEFAULT_DATE_FORMAT)

export const getRelativeTime = (
  firstDate: dayjs.ConfigType,
  secondDate: dayjs.ConfigType
) => dayjs(firstDate).to(dayjs(secondDate))

const quarters: Record<number, [number, number]> = {
  1: [1, 3],
  2: [4, 6],
  3: [7, 9],
  4: [10, 12]
}

const getQuarter = (date: Dayjs): number => Math.floor(date.month() / 3 + 1)

export const quarterStart = today
  .set('month', quarters[getQuarter(today)][0] - 1)
  .startOf('month')

export const quarterEnd = today
  .set('month', quarters[getQuarter(today)][1] - 1)
  .endOf('month')

export const isAfter = (date: dayjs.ConfigType): boolean =>
  dayjs().isAfter(date)

export const isBefore = (date: dayjs.ConfigType): boolean =>
  dayjs().isBefore(date)

export const formatDateOrEmpty = (date?: string | null) =>
  date ? dayjs(date) : undefined

export const formatDateToISOFormat = (
  isoInput: dayjs.ConfigType,
  inputFormat?: string
) =>
  isoInput
    ? dayjs(isoInput, inputFormat).format(DEFAULT_ISO_DATE_FORMAT)
    : undefined

export const getDaysRangeDate = (
  days: number
): { dateFrom: string; dateTo: string } => {
  const dateTo = dayjs().format(DEFAULT_ISO_DATE_FORMAT)
  const dateFrom = dayjs()
    .subtract(days, 'days')
    .format(DEFAULT_ISO_DATE_FORMAT)

  return { dateFrom, dateTo }
}

export const convertIso8601InDateFormat = (value?: string | null) => {
  const isoDuration = value ? dayjs.duration(value) : null

  const dueDate = isoDuration
    ? `${isoDuration.days()} ${isoDuration.hours()}:${isoDuration.minutes()}:00`
    : null

  return dueDate
}

export const convertDateFormatInIso8601 = (value?: string | null) => {
  const durationFormat =
    /^(?:(?<days>\d+)\s+)?(?<hours>\d{2}):(?<minutes>\d{2}):(?<seconds>\d{2})$/

  const dueDate = value
    ? dayjs.duration(durationFormat.exec(value)?.groups || {}).toISOString()
    : null

  return dueDate
}

export const isTime = (s: string | undefined) =>
  !!s && dayjs(s, DEFAULT_FULL_TIME_FORMAT).isValid()

export const getAge = (birthDate?: dayjs.ConfigType) => {
  const birthdate = dayjs(birthDate)
  const today = dayjs()

  return today.diff(birthdate, 'year')
}
